import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  Loader2,
  Users,
  FolderKanban,
  Clock,
  TrendingUp,
  DollarSign,
  BarChart2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (n ?? 0).toLocaleString(undefined, { maximumFractionDigits: 1 });
const fmtMoney = (n) => '$' + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const pct = (a, b) => b > 0 ? Math.min(100, Math.round((a / b) * 100)) : 0;

function StatCard({ title, value, sub, icon: Icon, color = 'text-primary', warn }) {
  return (
    <Card>
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${warn ? 'text-destructive' : color}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-primary/10 ${color}`}>
            <Icon className="w-5 h-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ProgressBar({ value, max, colorClass = 'bg-primary' }) {
  const p = pct(value, max);
  const isOver = p >= 90;
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-muted-foreground mb-1">
        <span>{fmt(value)} / {fmt(max)} h</span>
        <span className={isOver ? 'text-destructive font-semibold' : ''}>{p}%</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${isOver ? 'bg-destructive' : colorClass}`}
          style={{ width: `${Math.min(p, 100)}%` }}
        />
      </div>
    </div>
  );
}

const DONUT_COLORS = [
  '#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4',
  '#8b5cf6', '#ec4899', '#14b8a6', '#84cc16', '#f97316',
];
const OTHER_COLOR = '#f59e0b'; // amber — system "Other" task always identifiable

// Fill monthly trend from first data month (or earliest project start) through
// the later of: today OR the latest project end date. Zero-fills any gaps.
const fillMonthsToProjectEnd = (trend, earliestStart, latestEnd) => {
  const today = new Date();
  const todayMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

  // Determine end: max(today, latestEnd)
  let endMonth = todayMonth;
  if (latestEnd) {
    const endFromProject = latestEnd.slice(0, 7); // "YYYY-MM"
    if (endFromProject > endMonth) endMonth = endFromProject;
  }

  // Determine start: min(first data month, earliestStart)
  let startMonth = trend.length > 0 ? trend[0].month : todayMonth;
  if (earliestStart) {
    const startFromProject = earliestStart.slice(0, 7);
    if (startFromProject < startMonth) startMonth = startFromProject;
  }

  const dataMap = Object.fromEntries(trend.map(t => [t.month, t]));
  const result = [];
  // Use UTC dates to avoid timezone month-boundary issues
  let y = parseInt(startMonth.slice(0, 4));
  let m = parseInt(startMonth.slice(5, 7)); // 1-indexed
  const [endY, endM] = [parseInt(endMonth.slice(0, 4)), parseInt(endMonth.slice(5, 7))];

  while (y < endY || (y === endY && m <= endM)) {
    const key = `${y}-${String(m).padStart(2, '0')}`;
    result.push(dataMap[key] || { month: key, planned: 0, actual: 0 });
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return result;
};

export default function ClientDashboardPage() {
  const { user } = useAuth();
  const clientId = user?.managed_client_ids?.[0];

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clientId) { setLoading(false); return; }
    const fetch = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/clients/${clientId}/dashboard`);
        setData(res.data);
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [clientId]);

  if (!clientId) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 text-muted-foreground">
        <BarChart2 className="w-12 h-12 opacity-30" />
        <p className="text-lg font-medium">No client assigned</p>
        <p className="text-sm">Ask your administrator to assign you to a client.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data) return null;

  const hoursUsedPct = pct(data.total_actual_hours, data.total_allocated_hours);
  const budgetUsedPct = pct(data.utilized_budget, data.total_budget);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{data.client_name} — Dashboard</h1>
        <p className="text-muted-foreground text-sm mt-1">Live metrics across all projects for this client</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Projects" value={data.total_projects} icon={FolderKanban} sub="active projects" />
        <StatCard title="Employees" value={data.total_employees} icon={Users} sub="assigned via projects" />
        <StatCard
          title="Hours Actual"
          value={`${fmt(data.total_actual_hours)} h`}
          icon={Clock}
          sub={`of ${fmt(data.total_allocated_hours)} h allocated`}
          warn={hoursUsedPct >= 90}
        />
        <StatCard
          title="Planned to Date"
          value={`${fmt(data.total_planned_to_date)} h`}
          icon={TrendingUp}
          sub="cumulative plan so far"
        />
      </div>

      {/* Budget Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Hours Utilization
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ProgressBar value={data.total_actual_hours} max={data.total_allocated_hours} />
            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
              <span>Planned to date: <strong>{fmt(data.total_planned_to_date)} h</strong></span>
              <span>Remaining: <strong>{fmt(data.total_allocated_hours - data.total_actual_hours)} h</strong></span>
            </div>
          </CardContent>
        </Card>
        {data.total_budget > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-emerald-500" />
                Budget Utilization
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ProgressBar
                value={data.utilized_budget}
                max={data.total_budget}
                colorClass="bg-emerald-500"
              />
              <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                <span>Utilized: <strong className="text-emerald-600">{fmtMoney(data.utilized_budget)}</strong></span>
                <span>Total budget: <strong>{fmtMoney(data.total_budget)}</strong></span>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Row: Two donut charts side by side ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Donut 1: Hours by Task */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Hours by Task</CardTitle>
          </CardHeader>
          <CardContent>
            {data.hours_by_task.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No task data yet</div>
            ) : (() => {
              const total = data.hours_by_task.reduce((s, t) => s + t.hours, 0);
              const taskPct = data.hours_by_task.map(t => ({
                ...t,
                pct: total > 0 ? Math.round((t.hours / total) * 100) : 0,
              }));
              let nsi = 0;
              const colors = taskPct.map(t => t.is_system ? OTHER_COLOR : DONUT_COLORS[nsi++ % DONUT_COLORS.length]);
              return (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={taskPct} cx="50%" cy="50%" innerRadius="50%" outerRadius="75%"
                        dataKey="pct" nameKey="task_name" paddingAngle={2}>
                        {taskPct.map((_, i) => <Cell key={i} fill={colors[i]} />)}
                      </Pie>
                      <Tooltip formatter={(v, n, p) => [`${v}%  (${p.payload.hours} h)`, p.payload.task_name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-2 space-y-1 max-h-28 overflow-y-auto">
                    {taskPct.map((t, i) => (
                      <div key={t.task_id} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: colors[i] }} />
                          <span className="truncate text-muted-foreground">
                            {t.task_name}{t.is_system && <span className="italic ml-1">(system)</span>}
                          </span>
                        </div>
                        <span className="font-medium ml-2 flex-shrink-0">{t.pct}%</span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>

        {/* Donut 2: Hours by Category (CAPS / OPS / Other) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Hours by Category (CAPS / OPS)</CardTitle>
          </CardHeader>
          <CardContent>
            {data.hours_by_task.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No task data yet</div>
            ) : (() => {
              const total = data.hours_by_task.reduce((s, t) => s + t.hours, 0);
              const caps  = data.hours_by_task.filter(t => t.category === 'CAPS').reduce((s, t) => s + t.hours, 0);
              const ops   = data.hours_by_task.filter(t => t.category === 'OPS').reduce((s, t) => s + t.hours, 0);
              const other = data.hours_by_task.filter(t => !t.category || t.is_system).reduce((s, t) => s + t.hours, 0);
              const catData = [
                { name: 'CAPS',  hours: caps,  pct: total > 0 ? Math.round(caps  / total * 100) : 0, color: '#6366f1' },
                { name: 'OPS',   hours: ops,   pct: total > 0 ? Math.round(ops   / total * 100) : 0, color: '#22c55e' },
                { name: 'Other', hours: other, pct: total > 0 ? Math.round(other / total * 100) : 0, color: '#94a3b8' },
              ].filter(c => c.hours > 0);
              return (
                <>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={catData} cx="50%" cy="50%" innerRadius="50%" outerRadius="75%"
                        dataKey="pct" nameKey="name" paddingAngle={2}>
                        {catData.map((c, i) => <Cell key={i} fill={c.color} />)}
                      </Pie>
                      <Tooltip formatter={(v, n, p) => [`${v}%  (${fmt(p.payload.hours)} h)`, p.payload.name]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-2 space-y-1">
                    {catData.map(c => (
                      <div key={c.name} className="flex items-center justify-between text-xs">
                        <div className="flex items-center gap-1.5">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: c.color }} />
                          <span className="text-muted-foreground">{c.name}</span>
                        </div>
                        <span className="font-medium">{c.pct}%  <span className="text-muted-foreground font-normal">({fmt(c.hours)} h)</span></span>
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}
          </CardContent>
        </Card>
      </div>

      {/* ── Full-width Monthly Hours chart (extends to current month) ── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Monthly Hours — Planned vs Actual</CardTitle>
        </CardHeader>
        <CardContent>
          {data.monthly_trend.length === 0 ? (
            <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">No timesheet data yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={fillMonthsToProjectEnd(data.monthly_trend, data.earliest_project_start, data.latest_project_end)}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(m) => { try { return format(new Date(m + '-01'), 'MMM yy'); } catch { return m; } }}
                />
                <YAxis tick={{ fontSize: 11 }} unit="h" />
                <Tooltip
                  formatter={(v, name) => [`${v} h`, name]}
                  labelFormatter={(m) => { try { return format(new Date(m + '-01'), 'MMMM yyyy'); } catch { return m; } }}
                />
                <Legend />
                <Line type="monotone" dataKey="planned" name="Planned" stroke="#6366f1"
                  strokeWidth={2} dot={false} strokeDasharray="5 4" />
                <Line type="monotone" dataKey="actual" name="Actual" stroke="#22c55e"
                  strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Projects Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Projects Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {data.projects.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground text-sm">No projects linked to this client</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project</TableHead>
                  <TableHead className="text-right">Allocated</TableHead>
                  <TableHead className="text-right">Planned to Date</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead>Progress</TableHead>
                  {data.projects.some(p => p.budget > 0) && (
                    <>
                      <TableHead className="text-right">Budget</TableHead>
                      <TableHead className="text-right">Utilized</TableHead>
                    </>
                  )}
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.projects.map((p) => {
                  const used = pct(p.actual_hours, p.allocated_hours);
                  const onTrack = p.actual_hours <= p.planned_to_date * 1.1;
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="font-medium text-sm">{p.name}</div>
                        <div className="text-xs text-muted-foreground">{p.code}</div>
                      </TableCell>
                      <TableCell className="text-right text-sm">{fmt(p.allocated_hours)} h</TableCell>
                      <TableCell className="text-right text-sm text-muted-foreground">{fmt(p.planned_to_date)} h</TableCell>
                      <TableCell className="text-right text-sm font-medium">{fmt(p.actual_hours)} h</TableCell>
                      <TableCell className="min-w-[120px]">
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${used >= 90 ? 'bg-destructive' : 'bg-primary'}`}
                            style={{ width: `${Math.min(used, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{used}%</span>
                      </TableCell>
                      {data.projects.some(pp => pp.budget > 0) && (
                        <>
                          <TableCell className="text-right text-sm">{fmtMoney(p.budget)}</TableCell>
                          <TableCell className="text-right text-sm">{fmtMoney(p.utilized_budget)}</TableCell>
                        </>
                      )}
                      <TableCell>
                        {onTrack ? (
                          <Badge variant="outline" className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 text-xs">
                            <CheckCircle2 className="w-3 h-3 mr-1" />On Track
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs">
                            <AlertTriangle className="w-3 h-3 mr-1" />Review
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
